<template>
  <div
    ref="nsSlottedDropdown"
    class="ns-slotted-dropdown"
    :class="{
      disabled,
      'inline-label': inlineLabel,
      'auto-width': localWidth,
      nsd__small: size === 'small',
    }"
  >
    <div
      v-if="label"
      class="form-label"
      :class="{ 'label-error': errorMessage }"
    >
      {{ label }}
    </div>

    <div>
      <div
        class="dropdown-area"
        :class="{ 'open-up': openUp }"
        :style="{ width: localWidth }"
        @mouseleave="closeDropdown"
      >
        <div
          class="current-selection"
          :class="{
            'form-error': invalid || errorMessage,
            tall,
            opened: opened && !useModal,
          }"
          @click.stop="toggle"
        >
          <div
            v-if="(lockPlaceholder || selectedEmpty) && !selectedHtml"
            class="selected-item placeholder"
            :class="{ 'multi-line': multiLine }"
          >
            {{ placeholder }}
          </div>
          <div
            v-else-if="showCustomSelectedItem"
            class="selected-item"
            :class="{ 'multi-line': multiLine }"
          >
            <!-- @slot Slot for custom selected-item area -->
            <slot name="custom" />
          </div>
          <div
            v-else
            class="selected-item"
            :class="{ 'multi-line': multiLine }"
            v-html="selectedHtml"
          />
          <NsIcon
            v-if="!showCustomSelectedItem"
            class="arrow-icon"
            :class="{ opened: opened && !useModal }"
            icon-name="icon-narrow-angle-bottom-24"
          />
        </div>

        <div v-if="!useModal" v-show="opened" class="dropdown-content">
          <!-- @slot Slot for your own list without styles or select events -->
          <slot name="dropdown-content">
            <div ref="list" class="content-wrapper-mouse-helper">
              <NsSlottedDropdownItems
                :selected="selectedValue"
                :show-all="showAll"
                :dense="denseList"
                :using-custom="showCustomSelectedItem"
                @select="handleSelect"
              >
                <!-- @slot The list of elements to appear in the dropdown. -->
                <slot />
              </NsSlottedDropdownItems>
            </div>
          </slot>
        </div>
      </div>

      <p
        v-show="errorMessage || invalid"
        class="error-tag"
        :class="{ 'no-absolute': errorNoAbsolute }"
      >
        {{ errorMessage || requiredMessage }}
      </p>
    </div>

    <div v-if="useModal" ref="modal">
      <NsSlottedDropdownModal
        :open="opened"
        :title="label"
        :selected="selectedValue"
        :translations="translations"
        :using-custom="showCustomSelectedItem"
        :error-message="errorMessage || (invalid && requiredMessage) || ''"
        @select="handleSelect"
        @close="close"
      >
        <slot />
        <template #custom>
          <slot name="custom" />
        </template>
      </NsSlottedDropdownModal>
    </div>
  </div>
</template>

<script>
/**
 * Original Author: Dallin Anderson
 * Original Team: BFFs
 */
import { NsIcon } from '@nuskin/ns-icon';
import NsSlottedDropdownModal from './NsSlottedDropdownModal.vue';
import NsSlottedDropdownItems from './NsSlottedDropdownItems.vue';

/**
 * NsSlottedDropdown
 *
 * How to use: slot in a list of dom elements and listen to 'select' event.
 * Event contains '(value, index)'. 'value' will be index unless 'data-value'
 * attribute is used on root of each list element
 *
 * @event select  When a selection occurs this event emits a function with
 * params: (value, index)
 */
export default {
  name: 'NsSlottedDropdown',
  components: { NsIcon, NsSlottedDropdownModal, NsSlottedDropdownItems },
  model: {
    prop: 'selected',
    event: 'select',
  },
  props: {
    /**
     * Runs validation after closing the dropdown
     */
    autovalidate: Boolean,
    /**
     * List items have a smaller height
     */
    denseList: Boolean,
    /**
     * Sets the dropdown to disabled
     */
    disabled: Boolean,
    /**
     * Error message. Will show if truthy.
     */
    errorMessage: {
      type: String,
      default: '',
    },
    /**
     * By default the error message will be absolute-position, use this to override.
     */
    errorNoAbsolute: Boolean,
    /**
     * Label is inline
     */
    inlineLabel: Boolean,
    /**
     * Label copy for the dropdown
     */
    label: {
      type: String,
      default: '',
    },
    /**
     * Shows placeholder no matter the selection
     */
    lockPlaceholder: Boolean,
    /**
     * Uses smaller font
     */
    multiLine: Boolean,
    /**
     * Placeholder text
     */
    placeholder: {
      type: String,
      default: '',
    },
    /**
     * The error message shown when the input is invalid and required
     */
    requiredMessage: {
      type: String,
      default: 'Please choose an item.',
    },
    /**
     * Either the index or the value (data-value attribute) of the item that
     * should be in selected state
     */
    selected: {
      type: [String, Number],
      default: '',
    },
    /**
     * Shows the entire menu (no scroll)
     */
    showAll: Boolean,
    /**
     * Shows the 'custom' slot instead of 'selectedHtml' for the selected item
     */
    showCustomSelectedItem: Boolean,
    /**
     * The size of the selector. Options are 'large' and 'small'
     */
    size: {
      type: String,
      default: 'large',
    },
    /**
     * There are designs with a taller dropdown with button next to it
     */
    tall: Boolean,
    /**
     * Translations
     */
    translations: {
      type: Object,
      default() {
        return {};
      },
    },
    /**
     * Sets width of dropdown (excluding label). Alternate option is to use css
     * width on component (includes label).
     */
    width: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      /**
       * Set to true if you want to show the error message
       */
      invalid: false,
      /**
       * State of the content box. Use toggle() or close() to set this value
       */
      opened: false,
      /**
       * If true, the dropdown will open upwards
       */
      openUp: false,
      /**
       * True as long as the dropdown has never been opened. As soon as the user
       * opens the dropdown once this becomes false.
       */
      pristine: true,
      /**
       * Holds html of which dropdown item was selected
       */
      selectedHtml: null,
      /**
       * Holds actual selected value, whether from parent or dropdown
       */
      selectedValue: this.selected || '',
      /**
       * Uses modal instead of dropdown for selection when mobile
       */
      useModal: this.isMobileDevice(),
    };
  },
  computed: {
    selectedEmpty() {
      return this.selected == null || this.selected === '';
    },
    localWidth() {
      if (this.width) {
        return +this.width ? this.width + 'px' : this.width;
      }
      return null;
    },
  },
  watch: {
    selected(value) {
      this.selectedValue = value;
      if (this.autovalidate && !this.pristine) {
        this.validate();
      }
    },
    selectedValue: {
      immediate: true,
      async handler(value) {
        if (!value) {
          return;
        }

        await this.$nextTick();
        if (this.useModal) {
          this.selectedHtml = this.$refs.modal
            ? (this.$refs.modal.querySelector(`[data-value="${value}"]`) || {})
                .outerHTML
            : null;
        } else {
          this.selectedHtml = this.$refs.list
            ? (this.$refs.list.querySelector(`[data-value="${value}"]`) || {})
                .outerHTML
            : null;
        }
      },
    },
  },
  methods: {
    /**
     * Reset the dropdown to an unselected/initial state
     */
    clear() {
      // Clear the values and text
      this.selectedValue = '';
      this.emitSelect('');
    },
    /**
     * Closes the dropdown/modal and validates if necessary
     */
    close() {
      this.opened = false;
      if (this.autovalidate && !this.pristine) {
        this.validate();
      }
    },
    /**
     * Close the dropdown content container
     */
    closeDropdown() {
      if (!this.useModal) {
        this.close();
      }
    },
    /**
     * Emits an event whenever an item is selected
     */
    emitSelect(value, index) {
      /**
       * Fired when a menu item is selected: function(value: string|number, index: number) => void
       *
       * @type {select}
       * @property {(string|number)} value data-value attribute of the selected item, or index
       * @property {number} index index of the selected item
       */
      this.$emit('select', value, index);
    },
    /**
     * Sets the selection
     */
    handleSelect(value, index) {
      this.emitSelect(value, index);
      if (value !== 'custom') {
        this.selectedValue = value;
        this.emitSelect(value, index);
        this.close();
      }
    },
    /**
     * Checks if device is mobile (will use modal if so)
     */
    isMobileDevice() {
      return (
        typeof window.orientation !== 'undefined' ||
        navigator.userAgent.includes('IEMobile')
      );
    },
    /**
     * Show errors if invalid. This is used by ns-form for showing or clearing
     * out errors.
     */
    showErrors() {
      this.validate();
    },
    /**
     * Opens and closes the content area if not disabled.
     */
    toggle() {
      if (!this.disabled) {
        // if opening, check whether to open up or down
        if (!this.opened) {
          const clientY = this.$refs.nsSlottedDropdown.getBoundingClientRect()
            .top;
          this.openUp = clientY > window.innerHeight / 2;
        }

        this.opened = !this.opened;
        this.pristine = false;

        if (this.autovalidate && !this.opened) {
          this.validate();
        }
        if (this.useModal && document.activeElement) {
          document.activeElement.blur();
        }
      }
    },
    /**
     * Simple validation to see if anything was selected or not
     */
    validate() {
      if (this.selectedEmpty) {
        this.invalid = true;
      } else {
        this.invalid = false;
      }
      return !this.invalid;
    },
  },
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/other";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";
@use '~@nuskin/ns-core-styles/src/scss/global.mixins';

* {
  box-sizing: border-box;
  -webkit-font-smoothing: antialiased;
}

.ns-slotted-dropdown {
  position: relative;
  display: block;
  width: 150px;
  font-family: map.get(typographyVariables.$typography, font-family-primary);
  user-select: none;
  outline: none;

  &.disabled {
    pointer-events: none;
    cursor: not-allowed;

    .form-label {
      color: map.get(colors.$themeColors, color-primary-input-disabled);
    }

    .current-selection {
      color: map.get(colors.$themeColors, color-primary-input-disabled);
      border-color: map.get(colors.$themeColors, color-primary-input-disabled);
    }

    .arrow-icon {
      color: map.get(colors.$themeColors, color-primary-input-disabled);
    }
  }

  &.inline-label {
    display: flex;
    justify-content: center;
    align-items: center;

    .form-label {
      margin: 0;
      margin-right: 10px;
    }

    .error-tag {
      left: unset;
    }
  }

  &.auto-width {
    width: auto;

    .dropdown-area {
      flex: unset;
    }
  }

  &.nsd__small {
    .current-selection {
      min-height: 35px;
      height: 35px;
    }
  }
}

.form-label {
  margin: 0 0 5px;
  text-align: left;

  &.label-error {
    color: #d0021b;
  }
}

.dropdown-area {
  position: relative;
  flex: 1;
  cursor: pointer;
}

.open-up {
  .current-selection.opened {
    border-radius: 0px;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  .dropdown-content {
    bottom: 100%;
    border: 1px solid map.get(colors.$themeColors, color-interactive-8);
    border-bottom: none;
  }
}

.current-selection {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
  border: 1px solid map.get(colors.$themeColors, color-interactive-8);
  border-radius: 0px;
  min-height: 48px;
  height: 48px;
  width: 100%;
  color: map.get(colors.$themeColors, color-interactive-7);
  background-color: white;
  transition: map.get(other.$otherUtils, global-transition);
  // transition: border 0s;
  outline: none;

  &.opened {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  &.form-error,
  &.form-error:focus {
    background-color: white;
    border: 1px solid #d0021b;
  }
}

.selected-item {
  display: flex;
  align-items: center;
  // @include p-medium;

  &.placeholder {
    display: block;
    color: map.get(colors.$themeColors, color-interactive-8);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.multi-line {
    @include global.p-small;
    font-family: map.get(typographyVariables.$typography, font-family-primary);
    font-weight: map.get(typographyVariables.$typography, font-weight-regular);
    font-size: 14px;
    line-height: 18px;
  }
}

.arrow-icon {
  flex-shrink: 0;
  margin-left: 8px;
  width: 14px;
  height: 14px;
  color: map.get(colors.$themeColors, color-interactive-7);
  transition: map.get(other.$otherUtils, global-transition);

  &.opened {
    transform: scaleY(-1);
  }
}

.dropdown-content {
  position: absolute;
  z-index: map.get(other.$otherUtils, z-dropdown);
  border: 1px solid map.get(colors.$themeColors, color-interactive-8);
  border-top: none;
  width: 100%;
  background-color: white;
  font-family: map.get(typographyVariables.$typography, font-family-primary);
  transition: map.get(other.$otherUtils, global-transition);
}

// This provides a bigger clickable area so menu doesn't close unintentionally
div.content-wrapper-mouse-helper {
  margin-left: -24px;
  margin-right: -24px;
  padding-left: 24px;
  padding-right: 24px;
  width: calc(100% + 48px);
}

.error-tag {
  display: block;
  margin: 0;
  width: max-content;
  color: #d0021b;
  font-size: 12px;
  line-height: 22px;
  letter-spacing: -0.1px;

  &:not(.no-absolute) {
    position: absolute;
    top: 100%;
    left: 0;
  }
}

@media (min-width: 768px) {
  .selected-item {
    @include global.p-small;
  }
}

@media (min-width: 1024px) {
  .current-selection {
    height: auto;
    min-height: 35px;

    &.tall {
      height: 48px;
    }
  }
}
</style>
